import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { questionService, CreateQuestionRequest } from '@/features/admin/services/questionService';
import { categoryService, CategoryDto } from '@/features/admin/services/categoryService';
import { uploadService } from '@/features/admin/services/uploadService';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Image as ImageIcon } from 'lucide-react';

const createQuestionSchema = z.object({
  categoryId: z.string().min(1, 'الفئة مطلوبة'),
  questionTextAr: z.string().min(5, 'نص السؤال يجب أن يكون 5 أحرف على الأقل'),
  questionText: z.string().optional(),
  answerAr: z.string().min(1, 'الإجابة مطلوبة'),
  answer: z.string().optional(),
  difficulty: z.enum(['Easy', 'Medium', 'Hard'], { required_error: 'الصعوبة مطلوبة' }),
  timeLimit: z.number().min(5).max(120).default(30),
  explanation: z.string().optional(),
  explanationAr: z.string().optional(),
  isActive: z.boolean().default(false),
  imageSize: z.enum(['small', 'medium', 'large']).optional(),
  enableForNewUsers: z.boolean().default(false),
  isPromotionQuestion: z.boolean().default(false),
});

type CreateQuestionFormData = z.infer<typeof createQuestionSchema>;

export const CreateQuestionPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateQuestionFormData>({
    resolver: zodResolver(createQuestionSchema),
    defaultValues: {
      difficulty: 'Medium',
      timeLimit: 30,
      isActive: false,
      imageSize: 'medium',
      enableForNewUsers: false,
      isPromotionQuestion: false,
    },
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await categoryService.getAllCategories({ pageSize: 1000 });
      setCategories(data.items.filter(c => c.isActive));
    } catch (error: any) {
      toast.error('فشل تحميل الفئات');
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const onSubmit = async (data: CreateQuestionFormData) => {
    try {
      const request: CreateQuestionRequest = {
        categoryId: data.categoryId,
        questionText: data.questionText || data.questionTextAr,
        questionTextAr: data.questionTextAr,
        difficulty: data.difficulty,
        // نرسل الإجابة كخيار واحد صحيح
        options: [
          {
            text: data.answer || data.answerAr,
            textAr: data.answerAr,
            isCorrect: true,
          },
        ],
        timeLimit: data.timeLimit,
        explanation: data.explanation,
        explanationAr: data.explanationAr,
        isActive: data.isActive,
        imageUrl: imageUrl || undefined,
        imageSize: data.imageSize,
        enableForNewUsers: data.enableForNewUsers,
        isPromotionQuestion: data.isPromotionQuestion,
      };

      await questionService.createQuestion(request);
      toast.success('تم إنشاء السؤال بنجاح');
      navigate('/admin/questions');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل إنشاء السؤال');
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-accent-dark mb-2">إضافة سؤال جديد</h1>
        <p className="text-accent-dark/70">إنشاء سؤال جديد للعبة (سؤال وجواب)</p>
      </div>

      <Card className="border-2 border-accent-greenLight/20">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark">معلومات السؤال</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الفئة *
              </label>
              {loadingCategories ? (
                <LoadingSpinner size="sm" />
              ) : (
                <select
                  {...register('categoryId')}
                  className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
                >
                  <option value="">اختر الفئة</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nameAr || cat.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.categoryId && (
                <p className="text-sm text-accent-red mt-1">{errors.categoryId.message}</p>
              )}
            </div>

            {/* Question Text Arabic - Required */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                نص السؤال (عربي) *
              </label>
              <textarea
                {...register('questionTextAr')}
                placeholder="أدخل نص السؤال بالعربية..."
                rows={3}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
              {errors.questionTextAr && (
                <p className="text-sm text-accent-red mt-1">{errors.questionTextAr.message}</p>
              )}
            </div>

            {/* Question Text English - Optional */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                نص السؤال (إنجليزي)
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              <textarea
                {...register('questionText')}
                placeholder="Enter question text in English..."
                rows={2}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            {/* Answer Arabic - Required */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الإجابة (عربي) *
              </label>
              <Input
                {...register('answerAr')}
                placeholder="أدخل الإجابة الصحيحة..."
                className="text-lg"
              />
              {errors.answerAr && (
                <p className="text-sm text-accent-red mt-1">{errors.answerAr.message}</p>
              )}
            </div>

            {/* Answer English - Optional */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الإجابة (إنجليزي)
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              <Input
                {...register('answer')}
                placeholder="Enter answer in English..."
                className="text-lg"
              />
            </div>

            {/* Question Image - Optional */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                <ImageIcon className="w-4 h-4 inline ml-1" />
                صورة السؤال
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              
              {/* Image Size Selection */}
              <div className="flex gap-3 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    {...register('imageSize')}
                    value="small"
                    className="w-4 h-4 text-secondary"
                  />
                  <span className="text-sm">صغير</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    {...register('imageSize')}
                    value="medium"
                    className="w-4 h-4 text-secondary"
                  />
                  <span className="text-sm">متوسط</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    {...register('imageSize')}
                    value="large"
                    className="w-4 h-4 text-secondary"
                  />
                  <span className="text-sm">كبير</span>
                </label>
              </div>

              <ImageUpload
                value={imageUrl || undefined}
                onChange={(url) => setImageUrl(url)}
                onUpload={uploadService.uploadQuestionImage}
                placeholder="ارفع صورة للسؤال"
                maxSizeMB={5}
              />
              <p className="text-xs text-gray-500 mt-2">
                الصورة ستظهر مع السؤال بالحجم المحدد
              </p>
            </div>

            {/* Difficulty */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الصعوبة *
              </label>
              <select
                {...register('difficulty')}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              >
                <option value="Easy">سهل (200 نقطة)</option>
                <option value="Medium">متوسط (400 نقطة)</option>
                <option value="Hard">صعب (600 نقطة)</option>
              </select>
              {errors.difficulty && (
                <p className="text-sm text-accent-red mt-1">{errors.difficulty.message}</p>
              )}
            </div>

            {/* Time Limit */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الوقت المحدد (بالثواني)
              </label>
              <Input
                type="number"
                {...register('timeLimit', { valueAsNumber: true })}
                min="5"
                max="120"
                className="text-lg"
              />
              {errors.timeLimit && (
                <p className="text-sm text-accent-red mt-1">{errors.timeLimit.message}</p>
              )}
            </div>

            {/* Explanation Arabic */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                شرح الإجابة (عربي)
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              <textarea
                {...register('explanationAr')}
                placeholder="شرح الإجابة الصحيحة..."
                rows={2}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            {/* Explanation English */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                شرح الإجابة (إنجليزي)
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              <textarea
                {...register('explanation')}
                placeholder="Explanation of the correct answer..."
                rows={2}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            {/* Is Active */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isActive')}
                className="w-4 h-4 text-secondary focus:ring-secondary"
              />
              <label className="text-sm font-medium text-accent-dark">
                تفعيل السؤال مباشرة (بدون مراجعة)
              </label>
            </div>

            {/* تفعيل للمستخدمين الجدد */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('enableForNewUsers')}
                className="w-4 h-4 text-secondary focus:ring-secondary"
              />
              <label className="text-sm font-medium text-accent-dark">
                تفعيل للمستخدمين الجدد (يظهر السؤال في التجربة المجانية)
              </label>
            </div>

            {/* سؤال الترويج (الدعاية) */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isPromotionQuestion')}
                className="w-4 h-4 text-secondary focus:ring-secondary"
              />
              <label className="text-sm font-medium text-accent-dark">
                سؤال الترويج (الدعاية) - 36 سؤال مميزين لن يتكرروا (لا مجاني ولا دافع)
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/questions')}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                إلغاء
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-secondary hover:bg-secondary-dark text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    حفظ السؤال
                    <ArrowRight className="w-4 h-4 mr-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
